import React, { useState, useEffect } from "react";
import { FaSave } from "react-icons/fa";
import { toast } from "react-toastify";
import { useLanguage } from "../../context/LanguageContext";
import { API } from "../../utils";
import Loader from "../../components/Loader";

const Pricing = () => {
  const { t } = useLanguage();
  const [pricing, setPricing] = useState({
    petrolPrice: 0,
    dieselPrice: 0,
    oilPrices: {
      "20ml": 0,
      "40ml": 0,
      "60ml": 0,
    },
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        setSaving(true);
        const response = await API.get("/api/pricing");
        const result = response.data;
        if (result.success && result.data) {
          setPricing(result.data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setSaving(false);
      }
    };
    fetchPricing();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("oil_")) {
      const size = name.split("_")[1];
      setPricing({
        ...pricing,
        oilPrices: { ...pricing.oilPrices, [size]: Number(value) },
      });
    } else {
      setPricing({ ...pricing, [name]: Number(value) });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await API.post("/api/pricing", pricing);
      const result = response.data;
      if (result.success) {
        toast.success(t("pricing.pricingSaved"));
      } else {
        toast.error(result.error);
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message;
      toast.error(errMsg);
    } finally{
      setSaving(false);
    }
  };

  return (
    <>
     {saving && <Loader message="Saving Pricing..." />}
    <div className="max-w-5xl mx-auto font-sans">
      <h2 className="text-[22px] font-bold text-[#1e293b] mb-4">{t("pricing.title")}</h2>
      
      <div className="bg-white/80 backdrop-blur-xl rounded-[28px] shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-white/40 p-6">
        <form onSubmit={handleSave}>
          
          {/* Fuel Pricing Section */}
          <div className="bg-white/70 backdrop-blur-xl border border-green-100 rounded-[24px] overflow-hidden mb-6 shadow-sm">
            <div className="bg-green-50/70 backdrop-blur-md px-5 py-2 border-b border-green-100">
              <h3 className="text-[13px] leading-none font-bold text-green-700 tracking-wide">{t("pricing.fuelPricing")}</h3>
            </div>
            <div className="p-6 flex flex-col md:flex-row md:justify-between md:items-center bg-white relative">
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-6 mb-4 md:mb-0 md:pr-48">
                <div className="w-full">
                  <label className="block text-[11px] font-bold text-gray-700 mb-1.5">{t("pricing.petrolPrice")}</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    name="petrolPrice" 
                    value={pricing.petrolPrice || ""} 
                    onChange={handleChange} 
                    className="w-full border border-gray-200 rounded p-2 text-sm outline-none focus:border-green-500" 
                    required 
                  />
                </div>
                <div className="w-full">
                  <label className="block text-[11px] font-bold text-gray-700 mb-1.5">{t("pricing.dieselPrice")}</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    name="dieselPrice" 
                    value={pricing.dieselPrice || ""} 
                    onChange={handleChange} 
                    className="w-full border border-gray-200 rounded p-2 text-sm outline-none focus:border-green-500" 
                    required 
                  />
                </div>
              </div>
              <div className="hidden sm:block absolute right-0 top-0 bottom-0 bg-[#f0fdf4] w-48 flex items-center justify-center border-l border-green-200">
                <div className="flex items-center justify-center space-x-[-15px] pt-8">
                  <div className="bg-green-600 p-3 rounded-xl shadow-lg transform -rotate-12 z-10">
                    <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 22L15 22"/><path d="M18 22L21 22"/><path d="M7 22L7 9"/><path d="M11 22L11 9"/><path d="M15 22L15 9"/><path d="M19 22L19 18"/><path d="M5 9h14"/><path d="M5 5h14"/><path d="M5 22V5a2 2 0 012-2h10a2 2 0 012 2v17"/><path d="M11 13v3"/><path d="M15 13v3"/>
                    </svg>
                  </div>
                  <div className="bg-blue-600 p-3 rounded-xl shadow-lg transform rotate-12 z-0">
                    <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 22L15 22"/><path d="M18 22L21 22"/><path d="M7 22L7 9"/><path d="M11 22L11 9"/><path d="M15 22L15 9"/><path d="M19 22L19 18"/><path d="M5 9h14"/><path d="M5 5h14"/><path d="M5 22V5a2 2 0 012-2h10a2 2 0 012 2v17"/><path d="M11 13v3"/><path d="M15 13v3"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Oil Pricing Section */}
          <div className="bg-white/70 backdrop-blur-xl border border-orange-100 rounded-[24px] overflow-hidden mb-6 shadow-sm">
            <div className="bg-[#fff7ed] px-4 py-2 border-b border-orange-200">
              <h3 className="text-[13px] font-bold text-orange-600 tracking-wide">{t("pricing.oilPricing")}</h3>
            </div>
            <div className="p-6 flex flex-col md:flex-row md:justify-between md:items-center bg-white relative">
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-6 mb-4 md:mb-0 md:pr-48">
                <div className="w-full">
                  <label className="block text-[11px] font-bold text-gray-700 mb-1.5">{t("pricing.ml20Price")}</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    name="oil_20ml" 
                    value={pricing.oilPrices["20ml"] || ""} 
                    onChange={handleChange} 
                    className="w-full border border-gray-200 rounded p-2 text-sm outline-none focus:border-orange-500" 
                    required 
                  />
                </div>
                <div className="w-full">
                  <label className="block text-[11px] font-bold text-gray-700 mb-1.5">{t("pricing.ml40Price")}</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    name="oil_40ml" 
                    value={pricing.oilPrices["40ml"] || ""} 
                    onChange={handleChange} 
                    className="w-full border border-gray-200 rounded p-2 text-sm outline-none focus:border-orange-500" 
                    required 
                  />
                </div>
                <div className="w-full">
                  <label className="block text-[11px] font-bold text-gray-700 mb-1.5">{t("pricing.ml60Price")}</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    name="oil_60ml" 
                    value={pricing.oilPrices["60ml"] || ""} 
                    onChange={handleChange} 
                    className="w-full border border-gray-200 rounded p-2 text-sm outline-none focus:border-orange-500" 
                    required 
                  />
                </div>
              </div>
              <div className="hidden sm:block absolute right-0 top-0 bottom-0 bg-[#fff7ed] w-48 flex items-center justify-center border-l border-orange-200">
                <div className="flex items-end justify-center space-x-1 h-full py-6">
                  {/* Small Pouch */}
                  <div className="w-6 h-8 bg-orange-400 rounded-sm border-2 border-orange-600 shadow-sm flex items-center justify-center">
                    <span className="text-[6px] font-bold text-white uppercase leading-none">20ML</span>
                  </div>
                  {/* Medium Pouch */}
                  <div className="w-8 h-10 bg-rose-500 rounded-sm border-2 border-rose-700 shadow-sm flex items-center justify-center">
                    <span className="text-[8px] font-bold text-white uppercase leading-none">40ML</span>
                  </div>
                  {/* Large Pouch */}
                  <div className="w-10 h-12 bg-blue-600 rounded-sm border-2 border-blue-800 shadow-sm flex items-center justify-center">
                    <span className="text-[10px] font-bold text-white uppercase leading-none">60ML</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#eff6ff] text-blue-600 px-4 py-3 rounded text-[12px] font-semibold mb-6 border border-blue-100">
            {t("pricing.pricingNote")}
          </div>

          <div className="flex justify-center">
            <button 
              type="submit" 
              className="flex items-center space-x-2 bg-green-700 hover:bg-green-800 text-white px-8 py-2.5 rounded text-[13px] font-bold transition-colors duration-200"
            >
              <FaSave className="w-4 h-4" />
              <span>{t("pricing.savePricing")}</span>
            </button>
          </div>
          
        </form>
      </div>
    </div>
    </>
  );
};

export default Pricing;
